import { useCallback, useState } from 'react';

/**
 * Whether this browser has seen the guided first-run for an account — the web form of the phone's
 * `onboarding_controller.dart`, and the same half of the decision. Whether setup is *owed* is the
 * server's `setup_needed`; this records only that, in this browser, the user finished or skipped it,
 * so a skip is not re-offered on every visit. Keyed by user id so a second account on the same
 * browser is still offered its own setup.
 *
 * Browser storage can be unavailable (private windows, blocked site data): then the flag reads as
 * unseen and a skip lasts for the page's lifetime only — a repeat offer, never a missed one.
 */
const key = (userId: string) => `sixone8.onboardingSeen:${userId}`;

function read(userId: string): boolean {
  try {
    return localStorage.getItem(key(userId)) === '1';
  } catch {
    return false;
  }
}

export function useOnboardingSeen(userId: string | undefined): [boolean, () => void] {
  const [seen, setSeen] = useState<Record<string, boolean>>({});
  const value = userId ? (seen[userId] ?? read(userId)) : false;
  const markSeen = useCallback(() => {
    if (!userId) return;
    setSeen((prev) => ({ ...prev, [userId]: true }));
    try {
      localStorage.setItem(key(userId), '1');
    } catch {
      // Non-fatal: the in-memory flag still closes the flow for this page's lifetime.
    }
  }, [userId]);
  return [value, markSeen];
}
